// React
import { useState, useContext, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Kendo
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Form, Field, FormElement, FieldWrapper } from "@progress/kendo-react-form";
import { Input, NumericTextBox } from "@progress/kendo-react-inputs";
import { DatePicker } from '@progress/kendo-react-dateinputs';
import { Label } from "@progress/kendo-react-labels";

// Global Context
import { GlobalContext } from '../../../contexts/Global';

// Components
import Title from '../../../components/Page/Title';
import Tabs from '../../../components/Page/Tabs';
import Loader from '../../../components/Loader/Loader';
import Uploader from '../../../components/Forms/Uploader/Uploader';
import Repeater from '../../../components/Forms/Repeater/Repeater';
import Success from '../../../components/Page/Success';
import Error from '../../../components/Page/Error';
import DateInput from '../../../components/Forms/DateInput/DateInput';
import Switch from '../../../components/Forms/Switch/Switch';
import DropDownList from '../../../components/Forms/DropDownList/DropDownList';

// Utils
import { validateItalianTaxCode } from '../../../utils/CodiceFiscale';
import { appendArrayToFormData } from '../../../utils/Forms';
import province from '../../../utils/Province.json';

// Constants
import { MEMBER_CLASSES } from '../../../constants';

const MemberPage = () => {
    const subject_types = [
        { label: 'Persona fisica', value: 'individual' },
        { label: 'Persona giuridica', value: 'legal_entity' }
    ];

    const representative_roles = [
        { label: 'Legale rappresentante', value: 'legal_representative' },
        { label: 'Delegato con procura', value: 'delegated_signatory' }
    ];

    const declarations = [
        {
            text: "di avere i requisiti richiesti dallo Statuto e in particolare di avere i requisiti previsti dalle norme di attuazione dell'art. 22 della Direttiva n. 2018/2001, ivi compresi gli artt. 8, 31 e 32 del D.Lgs. n. 199/2021 e le relative disposizioni attuative, tra cui la Delibera ARERA n. 727/2022, il DM MASE n. 414/2023 e il DD MASE n. 22/2024, per essere membri delle configurazioni di autoconsumo diffuso (CER).",
            label: "Dichiara",
            field_name: "has_statutory_requirements"
        },
        {
            text: "di conoscere ed accettare le norme dello Statuto e dei Regolamenti adottati, di cui dichiara di aver preso visione, e di impegnarsi ad osservare le norme statutarie, i regolamenti e le determinazioni degli organi della Fondazione.",
            label: "Dichiara",
            field_name: "accepts_statute_regulations"
        },
        {
            text: "di sottoscrivere la quota di ammissione alla CER.",
            label: "Dichiara",
            field_name: "subscribes_admission_fee"
        },
        {
            text: "di dare mandato alla Fondazione per la costituzione e gestione della Comunità energetica rinnovabile, secondo quanto previsto dall'art. 3.4, let. e) del TIAD.",
            label: "Dichiara",
            field_name: "gives_foundation_mandate"
        },
        {
            text: "di individuare la Fondazione quale delegata responsabile del riparto dell'energia condivisa e di demandarle la gestione delle partite di incasso e pagamento verso il GSE e i venditori ai sensi dell'art. 32 del D. Lgs. n. 199/2021.",
            label: "Dichiara",
            field_name: "delegates_energy_sharing"
        },
        {
            text: "di autorizzare la Fondazione all'utilizzo e alla gestione dei propri dati di consumo.",
            label: "Dichiara",
            field_name: "authorizes_consumption_data"
        },
        {
            text: "di essere consapevole che sulla presente domanda deciderà il Consiglio di amministrazione con delibera inappellabile adottata con il voto favorevole della maggioranza dei suoi membri.",
            label: "Dichiara",
            field_name: "aware_board_decision"
        },
        {
            text: "di rinunciare alla propria quota di benefici eventualmente spettante per destinarla alle attività della Fondazione previste dallo Statuto e dal Regolamento adottato dal Consiglio di amministrazione, che il sottoscritto/a dichiara di conoscere e accettare.",
            label: "Dichiara",
            field_name: "waives_benefits_quota"
        },
        {
            text: "a fornire tutti gli ulteriori dati necessari per l'accesso al servizio per l'autoconsumo diffuso.",
            label: "Si impegna",
            field_name: "commits_additional_data"
        },
        {
            text: "all'utilizzo dei propri dati di misura dell'energia elettrica prelevata tramite il punto di connessione di cui sopra ai fini della determinazione dell'energia condivisa afferente all'edificio o condominio in cui è ubicato il suddetto punto di connessione e ad acconsentire, per proprio conto, al GSE di acquisire, per il tramite del sistema informativo integrato gestito dall'Acquirente unico spa, e di utilizzare i dati e le misure relativi alla/e fornitura/e di energia elettrica afferente al punto di connessione di cui sopra, ai fini della determinazione dell'energia condivisa e per lo svolgimento delle altre attività previste dalle disposizioni normative.",
            label: "Autorizza",
            field_name: "authorizes_measurement_data"
        },
        {
            text: "a dare la disponibilità, per proprio conto, alla partecipazione alle campagne di misura e monitoraggio condotte dalla società Ricerca sul Sistema Energetico spa ai sensi dell'art. 33, com. 1 del D.lgs. n. 199/2021 e dell'art. 42-bis, com. 7, del D.L. n. 162/2019.",
            label: "Autorizza",
            field_name: "authorizes_monitoring_campaigns"
        },
        {
            text: "l'acquisizione, per il tramite del sistema informativo integrato gestito dall'Acquirente unico spa, e l'utilizzo delle misure quarti orarie, anche pregresse, relative alla fornitura di energia elettrica afferente al predetto punto di connessione.",
            label: "Autorizza",
            field_name: "authorizes_quarterly_measures"
        },
        {
            text: "l'acquisizione, per il tramite del GSE, e l'utilizzo dei dati forniti nell'ambito delle dichiarazioni rese al GSE o acquisiti dal GSE tramite il sistema informativo integrato gestito dall'Acquirente unico spa e delle misure fornite al GSE dai gestori di rete;",
            label: "Autorizza",
            field_name: "authorizes_gse_data"
        },
        {
            text: "ad acquisire ogni potere necessario alla trasmissione e gestione dei dati, anche di natura personale, per proprio conto, e provvedere al loro aggiornamento e relativa comunicazione al GSE.",
            label: "Autorizza",
            field_name: "authorizes_data_management"
        },
    ];

    const navigate = useNavigate();
    const { memberId } = useParams();
    const { user } = useContext(GlobalContext);
    const [ isLoading, showLoader ] = useState(false);
    const [ member, setMember ] = useState({
        is_founder: false,
        is_gse: false,
        subject_type: subject_types.find(type => type.value === 'individual'),
        pods: [],
        ...Object.fromEntries(declarations.map(d => [d.field_name, false]))
    });
    const [ response, setResponse ] = useState(null);
    const [ randomId, setRandomId ] = useState('new');
    const [ deleteConfirmation, showDeleteConfirmation ] = useState(false);
    const [ validationDialog, setValidationDialog ] = useState({ show: false, errors: [] });
    const [ selectedTab, setSelectedTab ] = useState('dati');
    const memberFormRef = useRef(null)

    const tabs = [
        { id: 'dati', label: 'Dati' },
        { id: 'eventi', label: 'Eventi' }
    ];

    const loadMember = (memberId) => {
        showLoader(true);
        fetch(process.env.REACT_APP_API_BASE_URL + "/admin/cers/member/" + memberId, {
            headers: {
                Authorization: 'Bearer ' + user.access_token
            }
        })
            .then((response) => response.json())
            .then((response) => {
                showLoader(false);
                const member = { ...response.data.member }
                const dateFields = [ 'birth_date', 'agreement_date' ]
                dateFields.forEach(field => {
                    if (member[field] && typeof member[field] === 'string') {
                        member[field] = new Date(member[field]);
                    }
                });
                setMember(member);
                setRandomId((Math.random() + 1).toString(36));
            });
    }

    useEffect(()=> {
        if (memberId) {
            loadMember(memberId);
        }
    }, [ memberId ])

    const podsValidator = (value) => {
        if (!value || !Array.isArray(value) || value.length === 0) {
            return 'È necessario inserire almeno un punto di distribuzione (P.o.D.)';
        }

        // Check if at least one POD has a pod_code
        const hasValidPod = value.some(pod => pod && pod.pod_code && pod.pod_code.trim() !== '');
        if (!hasValidPod) {
            return 'È necessario inserire almeno un codice P.o.D. valido';
        }

        return '';
    };

    const validateForm = (data) => {
        const errors = [];

        if (!data.first_name || data.first_name.trim() === '') {
            errors.push({ field: 'Nome', message: 'Il nome è obbligatorio' });
        }

        if (!data.last_name || data.last_name.trim() === '') {
            errors.push({ field: 'Cognome', message: 'Il cognome è obbligatorio' });
        }

        // if (!data.tax_code || data.tax_code.trim() === '') {
        //     errors.push({ field: 'Codice Fiscale', message: 'Il codice fiscale è obbligatorio' });
        // }
        // else if (taxCodeValidator(data.tax_code)) {
        //     errors.push({ field: 'Codice Fiscale', message: taxCodeValidator(data.tax_code) });
        // }

        // if (!data.email || data.email.trim() === '') {
        //     errors.push({ field: 'Email', message: 'L\'indirizzo email è obbligatorio' });
        // }
        // else if (emailValidator(data.email)) {
        //     errors.push({ field: 'Email', message: emailValidator(data.email) });
        // }

        // if (!data.phone_number || data.phone_number.trim() === '') {
        //     errors.push({ field: 'Telefono', message: 'Il numero di telefono è obbligatorio' });
        // }
        // else if (phoneValidator(data.phone_number)) {
        //     errors.push({ field: 'Telefono', message: phoneValidator(data.phone_number) });
        // }

        // PODs validation - at least one required
        const podsError = podsValidator(data.pods);
        if (podsError) {
            errors.push({ field: 'Punti di distribuzione', message: podsError });
        }

        // // Subject type specific validations
        // if (data.subject_type?.value === 'legal_entity') {
        //     if (!data.company_name || data.company_name.trim() === '') {
        //         errors.push({ field: 'Denominazione', message: 'La denominazione è obbligatoria per le persone giuridiche' });
        //     }

        //     if (!data.company_tax_code || data.company_tax_code.trim() === '') {
        //         errors.push({ field: 'Codice Fiscale Azienda', message: 'Il codice fiscale aziendale è obbligatorio' });
        //     }

        //     if (!data.representative_role) {
        //         errors.push({ field: 'Ruolo rappresentativo', message: 'Il ruolo rappresentativo è obbligatorio per le persone giuridiche' });
        //     }
        // }

        // // Declaration validations - some are required
        // const requiredDeclarations = [
        //     'requisiti_statuto',
        //     'accettazione_statuto_regolamenti',
        //     'sottoscrizione_quota_ammissione'
        // ];

        // requiredDeclarations.forEach(fieldName => {
        //     if (!data[fieldName]) {
        //         const declaration = declarations.find(d => d.field_name === fieldName);
        //         if (declaration) {
        //             errors.push({
        //                 field: 'Dichiarazioni',
        //                 message: `È necessario accettare: "${declaration.text.substring(0, 50)}..."`
        //             });
        //         }
        //     }
        // });

        return errors;
    };

    const parseTaxCode = (e, formRenderProps) => {
        formRenderProps.onChange('tax_code', {
            value: e.value.toUpperCase()
        });

        const guessedData = validateItalianTaxCode(e.value);
        if (guessedData != false) {
            formRenderProps.onChange('birth_place', {
                value: guessedData.birth_place.name
            });

            formRenderProps.onChange('birth_province', {
                value: guessedData.birth_place.province
            });

            formRenderProps.onChange('birth_date', {
                value: guessedData.birth_date
            });
        }
    }

    const saveMember = (data, e) => {
        const validationErrors = validateForm(data);

        if (validationErrors.length > 0) {
            setValidationDialog({
                show: true,
                errors: validationErrors
            });
            return;
        }

        const formData = new FormData(e.target);

        const numericFields = [ 'registration_fee' ];

        Object.entries(data).forEach(([ key, value ]) => {
            if (value !== null) {
                if (value.constructor.name == 'Object') {
                    if (value.hasOwnProperty('value')) {
                        formData.delete(key);
                        formData.append(key, value.value);
                    }
                }
                else if (value.constructor.name == 'Array') {
                    if ([ 'id_document', 'last_invoice', 'resolution_document', 'mandate', 'signed_contract', 'payment_proof', 'other_documents', 'signature' ].includes(key)) {
                        formData.delete(key);
                        for (let file of value) {
                            if (file instanceof File) {
                                formData.append(key + '[]', file);
                            }
                            else {
                                formData.append(key + '[]', JSON.stringify(file));
                            }
                        }
                    }
                    else {
                        appendArrayToFormData(formData, key, value);
                    }
                }
                else if (value.constructor.name == 'Date') {
                    formData.set(key, value.toLocaleDateString('sv-SE'));
                }
                else if (typeof value === 'boolean') {
                    formData.set(key, value);
                }
                else if (typeof value === 'number' || numericFields.includes(key)) {
                    const numericValue = typeof value === 'number' ? value : parseFloat(value.toString().replace(',', '.'));
                    formData.set(key, isNaN(numericValue) ? value : numericValue);
                }
                else {
                    formData.set(key, value);
                }
            }
        });

        // console.log(data);
        // console.log('----------------------------------------------------');
        // for (const pair of formData.entries()) {
        //     console.log(pair[0], pair[1]);
        // }

        // return false;

        showLoader(true);

        const url = (
            member && member.id
            ?
            process.env.REACT_APP_API_BASE_URL + '/admin/cers/member/' + member.id
            :
            process.env.REACT_APP_API_BASE_URL + '/admin/cers/member'
        )

        fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + user.access_token
            },
            body: formData
        })
        .then((response) => response.json())
        .then((response) => {
            showLoader(false);
            setResponse(response);
        })
        .catch((err) => {
            console.log(err);
        });
    }

    const deleteMember = () => {
        showLoader(true);
        fetch(process.env.REACT_APP_API_BASE_URL + '/admin/cers/member/' + member.id, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + user.access_token
            }
        })
        .then((response) => response.json())
        .then((response) => {
            setResponse(response);
            showDeleteConfirmation(false);
            showLoader(false);
            navigate('/cers/members');
        })
        .catch((err) => {
            showLoader(false);
        });
    }

    const resetForm = () => {
        loadMember(memberId);
    }

    const requiredValidator = (value) => {
        if (!value || (typeof value === 'string' && value.trim() === '')) {
            return 'Questo campo è obbligatorio';
        }
        return '';
    };

    const UseResidenceAddress = ({ rowIndex }) => {
        const handleClick = () => {
            if (!memberFormRef.current) return;

            const residenceAddress = memberFormRef.current.valueGetter('residence_address');
            const residenceNumber = memberFormRef.current.valueGetter('residence_number');
            const residencePostalCode = memberFormRef.current.valueGetter('residence_postal_code');
            const residenceCity = memberFormRef.current.valueGetter('residence_city');
            const residenceProvince = memberFormRef.current.valueGetter('residence_province');

            const currentPods = memberFormRef.current.valueGetter('pods') || [];

            const updatedPods = currentPods.map((pod, index) => {
                if (index === rowIndex) {
                    return {
                        ...pod,
                        address: residenceAddress,
                        address_number: residenceNumber,
                        postal_code: residencePostalCode,
                        city: residenceCity,
                        province: residenceProvince
                    };
                }
                return pod;
            });

            memberFormRef.current.onChange('pods', { value: updatedPods });
        };

        return (
            <a className="k-font-italic k-font-size-xs k-text-secondary k-cursor-pointer" onClick={handleClick}>
                (Usa residenza)
            </a>
        );
    };

    return (
        <>
            <Tabs tabs={ tabs } selected={ selectedTab } onChange={ setSelectedTab } />
            <Title>Membro della Cers</Title>
            <div className="section">
                { member &&
                    <Form
                        key={ randomId }
                        ref={ memberFormRef }
                        initialValues={ member }
                        onSubmit={ saveMember }
                        render={(formRenderProps) => (
                            <FormElement>
                                <div className="k-d-grid k-grid-cols-5 k-gap-xl">
                                    <div className="k-col-span-3">
                                        <input type="hidden" name="changed" />

                                        <div className="k-d-grid k-grid-cols-12 k-gap-xl">
                                            {/* Classe: class */}
                                            <div className="k-col-span-2">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Classe</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field
                                                                name="class"
                                                                data={ MEMBER_CLASSES }
                                                                textField="label"
                                                                dataItemKey="value"
                                                                component={ DropDownList }
                                                            />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Tipo di soggetto: subject_type */}
                                            <div className="k-col-span-2">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Tipo di soggetto</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field
                                                                name="subject_type"
                                                                data={ subject_types }
                                                                textField="label"
                                                                dataItemKey="value"
                                                                component={ DropDownList }
                                                            />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* E' un fondatore?: is_founder */}
                                            <div className="k-col-span-2">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label k-justify-content-center">E' un fondatore?</Label>
                                                        <div className="k-form-field-wrap k-text-center">
                                                            <Field
                                                                name="is_founder"
                                                                onLabel="Si"
                                                                offLabel="No"
                                                                component={ Switch }
                                                            />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Data di stipula: agreement_date */}
                                            <div className="k-col-span-2">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Data di stipula</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="agreement_date" component={ DatePicker } dateInput={ DateInput } placeholder="" format="yyyy-MM-dd" />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Iscritto a GSE: is_gse */}
                                            <div className="k-col-span-2">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label k-justify-content-center">Iscritto a G.S.E.</Label>
                                                        <div className="k-form-field-wrap k-text-center">
                                                            <Field
                                                                name="is_gse"
                                                                data={ false }
                                                                onLabel="Si"
                                                                offLabel="No"
                                                                component={ Switch }
                                                            />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Quota di iscrizione: registration_fee */}
                                            <div className="k-col-span-2">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label k-justify-content-center">Quota d'iscrizione</Label>
                                                        <div className="k-form-field-wrap k-text-center">
                                                            <Field
                                                                name="registration_fee"
                                                                component={ NumericTextBox }
                                                                format="c2"
                                                                spinners={ false }
                                                            />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            <div className="k-col-span-12 k-pb-md k-border-b k-border-b-solid k-border-light k-text-uppercase k-font-bold">Dati Anagrafici</div>

                                            {/* Nome: first_name */}
                                            <div className="k-col-span-4">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Nome</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="first_name" component={ Input } />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Cognome: last_name */}
                                            <div className="k-col-span-4">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Cognome</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="last_name" component={ Input } />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Codice fiscale: tax_code */}
                                            <div className="k-col-span-4">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Codice Fiscale</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field
                                                                className="uppercase-input"
                                                                name="tax_code"
                                                                component={ Input }
                                                                onChange={(e) => { parseTaxCode(e, formRenderProps) }}
                                                            />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Data di nascita: birth_date */}
                                            <div className="k-col-span-4">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Data di nascita</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="birth_date" component={ DatePicker } dateInput={ DateInput } placeholder="" format="yyyy-MM-dd" />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Luogo di nascita: birth_place */}
                                            <div className="k-col-span-4">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Luogo di nascita</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="birth_place" component={ Input } />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Provincia di nascita: birth_province */}
                                            <div className="k-col-span-4">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Provincia di nascita</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field
                                                                name="birth_province"
                                                                data={ province }
                                                                textField="label"
                                                                dataItemKey="value"
                                                                component={ DropDownList }
                                                            />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            <div className="k-col-span-12 k-pb-md k-border-b k-border-b-solid k-border-light k-text-uppercase k-font-bold">Residenza</div>

                                            {/* Indirizzo di residenza: residence_address */}
                                            <div className="k-col-span-4">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Indirizzo</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="residence_address" component={ Input } />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Numero civico: residence_number */}
                                            <div className="k-col-span-1">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Civico</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="residence_number" component={ Input } />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Codice postale: residence_postal_code */}
                                            <div className="k-col-span-1">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">C.A.P.</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="residence_postal_code" component={ Input } />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Città di residenza: residence_city */}
                                            <div className="k-col-span-3">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Città</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="residence_city" component={ Input } />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Provincia di residenza: residence_province */}
                                            <div className="k-col-span-3">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Provincia</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field
                                                                name="residence_province"
                                                                data={ province }
                                                                textField="label"
                                                                dataItemKey="value"
                                                                component={ DropDownList }
                                                            />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Dati societari */}
                                            { (formRenderProps.valueGetter('subject_type')?.value == 'legal_entity' || formRenderProps.valueGetter('subject_type') == 'legal_entity') &&
                                                <>
                                                    <div className="k-col-span-12 k-pb-md k-border-b k-border-b-solid k-border-light k-text-uppercase k-font-bold">Soggetto giuridico</div>

                                                    {/* Nome azienda/studio: company_name */}
                                                    <div className="k-col-span-4">
                                                        <fieldset className="k-form-fieldset">
                                                            <FieldWrapper>
                                                                <Label className="k-form-label">Denominazione</Label>
                                                                <div className="k-form-field-wrap">
                                                                    <Field name="company_name" component={ Input } />
                                                                </div>
                                                            </FieldWrapper>
                                                        </fieldset>
                                                    </div>

                                                    {/* Ruolo rappresentativo: representative_role */}
                                                    <div className="k-col-span-4">
                                                        <fieldset className="k-form-fieldset">
                                                            <FieldWrapper>
                                                                <Label className="k-form-label">Ruolo rappresentativo</Label>
                                                                <div className="k-form-field-wrap">
                                                                    <Field
                                                                        name="representative_role"
                                                                        data={ representative_roles }
                                                                        textField="label"
                                                                        dataItemKey="value"
                                                                        component={ DropDownList }
                                                                    />
                                                                </div>
                                                            </FieldWrapper>
                                                        </fieldset>
                                                    </div>

                                                    <div className="k-col-span-4"></div>

                                                    {/* Codice fiscale: company_tax_code */}
                                                    <div className="k-col-span-4">
                                                        <fieldset className="k-form-fieldset">
                                                            <FieldWrapper>
                                                                <Label className="k-form-label">Codice Fiscale</Label>
                                                                <div className="k-form-field-wrap">
                                                                    <Field name="company_tax_code" component={ Input } />
                                                                </div>
                                                            </FieldWrapper>
                                                        </fieldset>
                                                    </div>

                                                    {/* Partita Iva: company_vat_number */}
                                                    <div className="k-col-span-4">
                                                        <fieldset className="k-form-fieldset">
                                                            <FieldWrapper>
                                                                <Label className="k-form-label">Partita IVA</Label>
                                                                <div className="k-form-field-wrap">
                                                                    <Field name="company_vat_number" component={ Input } />
                                                                </div>
                                                            </FieldWrapper>
                                                        </fieldset>
                                                    </div>

                                                    {/* Partita Iva: company_ateco_code */}
                                                    <div className="k-col-span-4">
                                                        <fieldset className="k-form-fieldset">
                                                            <FieldWrapper>
                                                                <Label className="k-form-label">codice ATECO</Label>
                                                                <div className="k-form-field-wrap">
                                                                    <Field name="company_ateco_code" component={ Input } />
                                                                </div>
                                                            </FieldWrapper>
                                                        </fieldset>
                                                    </div>

                                                    {/* Indirizzo: company_address */}
                                                    <div className="k-col-span-4">
                                                        <fieldset className="k-form-fieldset">
                                                            <FieldWrapper>
                                                                <Label className="k-form-label">Indirizzo</Label>
                                                                <div className="k-form-field-wrap">
                                                                    <Field name="company_address" component={ Input } />
                                                                </div>
                                                            </FieldWrapper>
                                                        </fieldset>
                                                    </div>

                                                    {/* Numero civico: company_address_number */}
                                                    <div className="k-col-span-1">
                                                        <fieldset className="k-form-fieldset">
                                                            <FieldWrapper>
                                                                <Label className="k-form-label">Civico</Label>
                                                                <div className="k-form-field-wrap">
                                                                    <Field name="company_address_number" component={ Input } />
                                                                </div>
                                                            </FieldWrapper>
                                                        </fieldset>
                                                    </div>

                                                    {/* Codice postale: company_postal_code */}
                                                    <div className="k-col-span-1">
                                                        <fieldset className="k-form-fieldset">
                                                            <FieldWrapper>
                                                                <Label className="k-form-label">C.A.P.</Label>
                                                                <div className="k-form-field-wrap">
                                                                    <Field name="company_postal_code" component={ Input } />
                                                                </div>
                                                            </FieldWrapper>
                                                        </fieldset>
                                                    </div>

                                                    {/* Città: company_city */}
                                                    <div className="k-col-span-3">
                                                        <fieldset className="k-form-fieldset">
                                                            <FieldWrapper>
                                                                <Label className="k-form-label">Città</Label>
                                                                <div className="k-form-field-wrap">
                                                                    <Field name="company_city" component={ Input } />
                                                                </div>
                                                            </FieldWrapper>
                                                        </fieldset>
                                                    </div>

                                                    {/* Provincia: company_province */}
                                                    <div className="k-col-span-3">
                                                        <fieldset className="k-form-fieldset">
                                                            <FieldWrapper>
                                                                <Label className="k-form-label">Provincia</Label>
                                                                <div className="k-form-field-wrap">
                                                                    <Field
                                                                        name="company_province"
                                                                        data={ province }
                                                                        textField="label"
                                                                        dataItemKey="value"
                                                                        component={ DropDownList }
                                                                    />
                                                                </div>
                                                            </FieldWrapper>
                                                        </fieldset>
                                                    </div>
                                                </>
                                            }

                                            <div className="k-col-span-12 k-pb-md k-border-b k-border-b-solid k-border-light k-text-uppercase k-font-bold">Recapiti</div>

                                            {/* Indirizzo email: email */}
                                            <div className="k-col-span-4">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Indirizzo email</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="email" component={ Input } />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* PEC: pec */}
                                            <div className="k-col-span-4">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Posta Elettronica Certificata (PEC)</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="pec" component={ Input } />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Numero di telefono: phone_number */}
                                            <div className="k-col-span-4">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Numero di telefono</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="phone_number" component={ Input } />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Punti di distribuzione: pods */}
                                            <div className="k-col-span-12 k-pb-md k-border-b k-border-b-solid k-border-light k-text-uppercase k-font-bold">Punti di distribuzione</div>

                                            <div className="k-col-span-12">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <div className="k-form-field-wrap">
                                                            <Field
                                                                name="pods"
                                                                component={ Repeater }
                                                                className="k-d-grid k-grid-cols-12 k-gap-xl"
                                                                fields={[
                                                                    {
                                                                        name: 'pod_code',
                                                                        label: 'P.o.D.',
                                                                        component: Input,
                                                                        className: 'k-col-span-2'
                                                                    },
                                                                    {
                                                                        name: 'cadastral_section',
                                                                        label: 'Sezione catastale',
                                                                        component: Input,
                                                                        className: 'k-col-span-2'
                                                                    },
                                                                    {
                                                                        name: 'sheet',
                                                                        label: 'Foglio',
                                                                        component: Input,
                                                                        className: 'k-col-span-1'
                                                                    },
                                                                    {
                                                                        name: 'parcel',
                                                                        label: 'Particella',
                                                                        component: Input,
                                                                        className: 'k-col-span-1'
                                                                    },
                                                                    {
                                                                        name: 'sub_unit',
                                                                        label: 'Subalterno',
                                                                        component: Input,
                                                                        className: 'k-col-span-1'
                                                                    },
                                                                    {
                                                                        name: 'nominal_power',
                                                                        label: 'Potenza (kWh)',
                                                                        component: Input,
                                                                        className: 'k-col-span-2'
                                                                    },
                                                                    {
                                                                        name: 'annual_consumption',
                                                                        label: 'Consumo annuale (kWh)',
                                                                        component: Input,
                                                                        className: 'k-col-span-2'
                                                                    },
                                                                    {
                                                                        name: 'address',
                                                                        label: 'Indirizzo',
                                                                        component: Input,
                                                                        className: 'k-col-span-4',
                                                                        labelAction: UseResidenceAddress
                                                                    },
                                                                    {
                                                                        name: 'address_number',
                                                                        label: 'Civico',
                                                                        component: Input,
                                                                        className: 'k-col-span-1'
                                                                    },
                                                                    {
                                                                        name: 'postal_code',
                                                                        label: 'C.A.P.',
                                                                        component: Input,
                                                                        className: 'k-col-span-1'
                                                                    },
                                                                    {
                                                                        name: 'city',
                                                                        label: 'Città',
                                                                        component: Input,
                                                                        className: 'k-col-span-3'
                                                                    },
                                                                    {
                                                                        name: 'province',
                                                                        label: 'Provincia',
                                                                        component: DropDownList,
                                                                        data: province,
                                                                        textField: 'label',
                                                                        dataItemKey: 'value',
                                                                        className: 'k-col-span-2',
                                                                        allowNull: true,
                                                                        nullText: ''
                                                                    }
                                                                ]}
                                                            />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            <div className="k-col-span-12 k-pb-md k-border-b k-border-b-solid k-border-light k-text-uppercase k-font-bold">Allegati</div>

                                            {/* Documento di identità: id_document */}
                                            <div className="k-col-span-6">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Documento di identità</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="id_document" component={ Uploader } />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Ultima bolletta: last_invoice */}
                                            <div className="k-col-span-6">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Ultima bolletta</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="last_invoice" component={ Uploader } />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Scansione mandato: mandate */}
                                            <div className="k-col-span-6">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Mandato</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="mandate" component={ Uploader } />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Scansione mandato: signed_contract */}
                                            <div className="k-col-span-6">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Contratto firmato</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="signed_contract" component={ Uploader } />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Delibera del soggetto giuridico: resolution_document */}
                                            <div className="k-col-span-6">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Delibera del soggetto giuridico</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="resolution_document" component={ Uploader } />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            {/* Prova di pagamento: payment_proof */}
                                            <div className="k-col-span-6">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Prova di pagamento</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="payment_proof" component={ Uploader } />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            <div className="k-col-span-12 k-pb-md k-border-b k-border-b-solid k-border-light k-text-uppercase k-font-bold">Altra documentazione</div>

                                            {/* Altri documenti: other_documents */}
                                            <div className="k-col-span-6">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <div className="k-form-field-wrap">
                                                            <Field name="other_documents" component={ Uploader } />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            <div className="k-col-span-12 k-pb-md k-border-b k-border-b-solid k-border-light k-text-uppercase k-font-bold">Firma</div>

                                            {/* Altri documenti: signature */}
                                            <div className="k-col-span-6">
                                                <fieldset className="k-form-fieldset">
                                                    <FieldWrapper>
                                                        <Label className="k-form-label">Firma</Label>
                                                        <div className="k-form-field-wrap">
                                                            <Field className="signature" name="signature" maxFiles={ 1 }  component={ Uploader } />
                                                        </div>
                                                    </FieldWrapper>
                                                </fieldset>
                                            </div>

                                            <div className="k-col-span-12">
                                                <div className="k-form-buttons k-d-flex k-justify-content-between k-flex-wrap">
                                                    <div className="k-d-flex k-gap-sm k-align-items-center">
                                                        <Button type="submit" rounded={ null } size="large" themeColor="primary" disabled={ !formRenderProps.modified }>SALVA</Button>
                                                        <Button type="button" rounded={ null } size="small" themeColor="primary" disabled={ !formRenderProps.modified } onClick={ () => resetForm() }>ANNULLA</Button>
                                                    </div>
                                                    <div>
                                                        <Button rounded={ null } size="small" themeColor="error" onClick={ () => showDeleteConfirmation(true) }>CANCELLA</Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    </div>

                                    <div className="k-col-span-2">
                                        <div className="k-d-grid k-grid-cols-12 k-gap-xl">
                                            { declarations.map((item, index) =>
                                                <div key={ 'declarations' + index } className="k-col-span-12">
                                                    <div className="k-d-grid k-grid-cols-12 k-gap-xl">
                                                        <div className="k-col-span-2">
                                                            <fieldset className="k-form-fieldset">
                                                                <FieldWrapper>
                                                                    <Label className="k-form-label k-justify-content-end">{ item.label }</Label>
                                                                    <div className="k-form-field-wrap k-text-right">
                                                                        <Field
                                                                            name={ item.field_name }
                                                                            defaultChecked={ false }
                                                                            onLabel="Si"
                                                                            offLabel="No"
                                                                            component={ Switch }
                                                                        />
                                                                    </div>
                                                                </FieldWrapper>
                                                            </fieldset>
                                                        </div>

                                                        <div className="k-col-span-10 k-pt-xl">{ item.text }</div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </FormElement>
                        )}
                    />
                }
            </div>

            <Success enter={ response } exit={ setResponse } />
            <Error enter={ response } exit={ setResponse } />

            { deleteConfirmation && (
                <Dialog onClose={ () => showDeleteConfirmation(false) }>
                    <p style={{ margin: "25px", textAlign: "center" }} >
                        Vuoi cancellare questo membro della CERS?
                    </p>
                    <DialogActionsBar>
                        <Button rounded={ null } size="large" themeColor="primary" onClick={ () => showDeleteConfirmation(false) }>ANNULLA</Button>
                        <Button rounded={ null } size="large" themeColor="error" onClick={ () => deleteMember() }>CANCELLA</Button>
                    </DialogActionsBar>
                </Dialog>
            )}

            { validationDialog.show && (
                <Dialog
                    onClose={() => setValidationDialog({ show: false, errors: [] })}
                    title="Errori di validazione"
                    width={ 500 }
                >
                    <div style={{ padding: "20px" }}>
                        <p style={{ marginBottom: "15px", fontWeight: "bold" }}>
                            Correggere i seguenti errori prima di salvare:
                        </p>
                        <ul style={{ paddingLeft: "20px" }}>
                            { validationDialog.errors.map((error, index) => (
                                <li key={ index } style={{ marginBottom: "8px" }}>
                                    <strong>{ error.field }:</strong> { error.message }
                                </li>
                            ))}
                        </ul>
                    </div>
                    <DialogActionsBar>
                        <Button
                            rounded={ null }
                            size="large"
                            themeColor="primary"
                            onClick={() => setValidationDialog({ show: false, errors: [] })}
                        >
                            OK
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}

            { isLoading && (<Loader />) }
        </>
    );
}

export default MemberPage;
